import { curvanceConfigFor } from '@1delta/data-sdk'
import { parseRawAmount } from '../../../utils/parsing'
import { isFailedCall } from '../../../utils/multicall'
import { createMarketUid } from '../../../utils'
import { getDisplayPrice, getOraclePrice } from '../utils/oraclePrice'
import { createMultiAccountTypeUserState } from '../utils'
import {
  LenderCrossPoolMeta,
  LenderToLenderCrossPoolMeta,
  UserData,
} from '../utils/types'
import { curvanceLenderKey } from '../../public-data/curvance/convertPublic'
import {
  CURVANCE_MARKET_READS,
  CURVANCE_READS_PER_TOKEN,
  getCachedCurvanceDiscovery,
} from './userCallBuild'

/**
 * Per-position Curvance detail attached to BOTH rows of a market (raw strings).
 *
 * The hold period is the reason this exists as a first-class field rather than
 * a derived display value: it is a hard on-chain gate that makes `repay`,
 * `redeem` and share transfers revert, and no other lender we integrate has
 * anything like it.
 */
export interface CurvancePositionInfo {
  /** The market this position lives in. */
  marketManager: string
  /** UNIX seconds when the 20-minute hold period expires. `0` = never entered. */
  holdPeriodEndsAt: string
  /** Whether the hold period is running RIGHT NOW — i.e. repay/redeem revert. */
  holdPeriodActive: boolean
  /** Total collateral value, WAD USD, at Curvance's pessimistic-LOW prices. */
  collateralValueUsdWad: string
  /** Maximum debt this collateral supports, WAD USD. */
  maxDebtUsdWad: string
  /** Current debt value, WAD USD, at pessimistic-HIGH prices. */
  debtValueUsdWad: string
  /** Per-leg raw balances, keyed by cToken address (lowercase). */
  tokens: {
    [cToken: string]: {
      /** cToken SHARES held (posted + idle). */
      shares: string
      /** cToken shares POSTED as collateral — the subset that backs a borrow. */
      collateralShares: string
      /** Debt in the leg's underlying units, interest accrued. */
      debt: string
      /** Assets per 1e18 shares at read time (divisor is WAD, not decimals). */
      exchangeRate: string
    }
  }
}

const WAD = 10n ** 18n

const big = (v: any): bigint => {
  try {
    if (typeof v === 'bigint') return v
    if (typeof v === 'number' || typeof v === 'string') return BigInt(v)
  } catch {}
  return 0n
}

const tupleAt = (v: any, i: number): bigint =>
  Array.isArray(v) ? big(v[i]) : 0n

/** `MIN_HOLD_PERIOD` — a protocol constant on every deployment. */
const HOLD_PERIOD_SECONDS = 1200n

/**
 * Returns a [converter, expectedCallCount] tuple for Curvance user data.
 *
 * Layout mirrors `buildCurvanceUserCall` (deterministic from the cached
 * discovery). Emits one `UserData` per market key
 * `CURVANCE_<chainId>_<MARKET_MANAGER>`, each with a single sub-account `'0'` —
 * positions are address-keyed, at most ONE per user per market.
 *
 * Two modelling notes:
 *
 *  - **Deposits are reported in the UNDERLYING, from POSTED collateral shares.**
 *    A user can hold cToken shares WITHOUT posting them (a plain 4626 deposit
 *    that is not backing anything), and those are not collateral. We publish
 *    `collateralPosted` converted through the market's exchange rate, and carry
 *    the raw idle/posted split in `curvanceInfo` — every write on the cToken is
 *    denominated in SHARES, so a caller with only the human number cannot build
 *    a transaction.
 *  - **`statusOf` is the protocol's own valuation**, at the pessimistic side
 *    per role (collateral low, debt high). It is carried verbatim in WAD USD
 *    rather than recomputed from our price map, because it IS what liquidation
 *    is measured against — a health factor derived from anything else would
 *    disagree with the chain at exactly the moment it matters.
 */
export const getCurvanceUserDataConverter = (
  lender: string,
  chainId: string,
  account: string,
  meta?: LenderToLenderCrossPoolMeta,
): [(data: any[]) => { [lender: string]: UserData } | undefined, number] => {
  const cfg = curvanceConfigFor(lender, chainId)
  // The SAME active set the build phase discovered — re-discovering here could
  // desync the slicer from the call list.
  const discovery = getCachedCurvanceDiscovery(chainId, lender, account)
  const positions = discovery?.positions ?? []
  const expected =
    cfg && positions.length > 0
      ? positions.reduce(
          (acc, p) =>
            acc +
            CURVANCE_MARKET_READS +
            p.activeTokens.length * CURVANCE_READS_PER_TOKEN,
          0,
        )
      : 0

  return [
    (data: any[]) => {
      if (!cfg || expected === 0) return undefined
      if (!data || data.length < expected) return undefined

      const out: { [lender: string]: UserData } = {}
      const nowSeconds = BigInt(Math.floor(Date.now() / 1000))
      let cursor = 0

      for (const p of positions) {
        const base = cursor
        cursor +=
          CURVANCE_MARKET_READS +
          p.activeTokens.length * CURVANCE_READS_PER_TOKEN

        const cooldownRaw = data[base]
        const status = data[base + 1]
        // A failed market read is NOT an empty position — skip rather than
        // report a zeroed one, which would render as a healthy empty account.
        if (isFailedCall(status) || !Array.isArray(status)) continue

        const cooldownTimestamp = isFailedCall(cooldownRaw)
          ? 0n
          : big(cooldownRaw)
        const holdPeriodEndsAt =
          cooldownTimestamp > 0n ? cooldownTimestamp + HOLD_PERIOD_SECONDS : 0n
        const holdPeriodActive = holdPeriodEndsAt > nowSeconds

        const lenderKey = curvanceLenderKey(lender, chainId, p.marketManager)
        const metaMap: LenderCrossPoolMeta | undefined = meta?.[lenderKey]
        if (!metaMap) continue

        const info: CurvancePositionInfo = {
          marketManager: p.marketManager,
          holdPeriodEndsAt: holdPeriodEndsAt.toString(),
          holdPeriodActive,
          collateralValueUsdWad: tupleAt(status, 0).toString(),
          maxDebtUsdWad: tupleAt(status, 1).toString(),
          debtValueUsdWad: tupleAt(status, 2).toString(),
          tokens: {},
        }

        const lendingPositions: Record<string, any> = { '0': {} }
        const hist: Record<string, any> = {
          '0': { totalDeposits24h: 0, totalDebt24h: 0 },
        }
        let rows = 0

        p.activeTokens.forEach(({ cToken, asset }, i) => {
          const tokenBase =
            base + CURVANCE_MARKET_READS + i * CURVANCE_READS_PER_TOKEN
          const shares = isFailedCall(data[tokenBase])
            ? 0n
            : big(data[tokenBase])
          const collateralShares = isFailedCall(data[tokenBase + 1])
            ? 0n
            : big(data[tokenBase + 1])
          const debtRaw = isFailedCall(data[tokenBase + 2])
            ? 0n
            : big(data[tokenBase + 2])
          const exchangeRate = isFailedCall(data[tokenBase + 3])
            ? 0n
            : big(data[tokenBase + 3])

          info.tokens[cToken.toLowerCase()] = {
            shares: shares.toString(),
            collateralShares: collateralShares.toString(),
            debt: debtRaw.toString(),
            exchangeRate: exchangeRate.toString(),
          }

          // The public rows are keyed on the UNDERLYING; `asset` was resolved
          // once during discovery. Pairing legs back to rows by POSITION would
          // silently swap them on a market where both legs are borrowable.
          const uid = createMarketUid(chainId, lenderKey, asset)
          const rowMeta = metaMap[uid]
          if (!rowMeta) return

          const assetDecimals = rowMeta.asset?.decimals ?? 18
          const display = getDisplayPrice(rowMeta)
          const oracle = getOraclePrice(rowMeta)
          const histPrice = rowMeta.price?.priceUsd24h ?? display

          // Shares → underlying. `exchangeRate()` is assets per 1e18 shares —
          // the divisor is WAD regardless of the cToken's own decimals — and it
          // drifts UP with accrued interest, so it is read live rather than
          // assumed 1:1.
          const toUnderlying = (s: bigint) =>
            exchangeRate > 0n ? (s * exchangeRate) / WAD : s
          const depositsRaw = toUnderlying(collateralShares)

          const depositsStr = parseRawAmount(
            depositsRaw.toString(),
            assetDecimals,
          )
          const depositsNum = Number(depositsStr)
          const debtStr = parseRawAmount(debtRaw.toString(), assetDecimals)
          const debtNum = Number(debtStr)

          lendingPositions['0'][uid] = {
            marketUid: uid,
            underlying: asset,
            deposits: depositsStr,
            debt: debtStr,
            debtStable: '0',
            depositsUSD: depositsNum * display,
            debtUSD: debtNum * display,
            debtStableUSD: 0,
            depositsUSDOracle: depositsNum * oracle,
            debtUSDOracle: debtNum * oracle,
            debtStableUSDOracle: 0,
            stableBorrowRate: '0',
            // In Curvance "collateral enabled" is not a per-market toggle: it
            // is whether shares are POSTED. Idle shares earn but back nothing.
            collateralEnabled: collateralShares > 0n,
            claimableRewards: 0,
            curvanceInfo: info,
          }
          hist['0'].totalDeposits24h += depositsNum * histPrice
          hist['0'].totalDebt24h += debtNum * histPrice
          rows++
        })

        if (rows === 0) continue

        const userData = createMultiAccountTypeUserState(
          { chainId, account, lendingPositions, modes: { '0': 0 } } as any,
          metaMap,
          hist,
        )
        if (userData && userData.data.length > 0) out[lenderKey] = userData
      }

      return Object.keys(out).length > 0 ? out : undefined
    },
    expected,
  ]
}
